import { useMemo, useState } from "react";
import { useEffect } from "react";
import { useDateFormatter } from "react-aria";
import { isToday, toCalendarDate } from "@internationalized/date";
import clsx from "clsx";

import { OfficeInformation } from "./OfficeInformation";
import { TimeslotLink } from "./TimeslotLink";
import {
  Timeframe,
  TimetableGroups,
  useTimetableContext,
  useTimetableData,
} from "./Timetable.hooks";
import { TimetableHeader } from "./TimetableHeader";

import { Button } from "~/components/Button";
import { Collapsible } from "~/components/Collapsible";
import { ChevronIcon, DIRECTION } from "~/components/Icons/ChevronIcon";
import { EveningIcon, MorningIcon, NoonIcon } from "~/components/Icons/DayPartIcons";
import { Spinner } from "~/components/Loader";
import { Text } from "~/components/Text";
import { TimetableLoaderData } from "~/modules/timetable/timetable.loader.server";
import { useSendCAEvent } from "~/routes/api+/enterprises.$enterprise.session-events";

export function TimetableMobile(props: TimetableLoaderData) {
  const { days, errors, groups, office, period } = useTimetableData(props);
  const { timeZone } = useTimetableContext();
  const sendCAEvent = useSendCAEvent();

  const weekdayFormatter = useDateFormatter({ timeZone, weekday: "short" });
  const dayFormatter = useDateFormatter({ timeZone, day: "numeric" });
  const fullDateFormatter = useDateFormatter({ timeZone, dateStyle: "full" });

  const views = useMemo(() => {
    return days.map((day) => {
      const [morningRows, noonRows, eveningRows] = getDayTimeframes(groups, day.id);

      return {
        dayId: day.id,
        data: [
          {
            name: `steps.Timetable.DayPart.morning`,
            Icon: MorningIcon,
            rows: morningRows,
          },
          {
            name: `steps.Timetable.DayPart.afternoon`,
            Icon: NoonIcon,
            rows: noonRows,
          },
          {
            name: `steps.Timetable.DayPart.evening`,
            Icon: EveningIcon,
            rows: eveningRows,
          },
        ],
      };
    });
  }, [groups, days]);

  const defaultSelectedDayId = useMemo(() => {
    const dayWithSlots = views.find((view) =>
      view.data.some((period) => period.rows && period.rows.length > 0),
    );

    return dayWithSlots?.dayId ?? days[0]?.id;
  }, [views, days]);

  const [selectedDayId, setSelectedDayId] = useState(defaultSelectedDayId);
  useEffect(() => {
    setSelectedDayId(defaultSelectedDayId);
  }, [defaultSelectedDayId]);

  const selectedDay = useMemo(
    () => days.find((day) => day.id === selectedDayId),
    [days, selectedDayId],
  );

  const dayViews = useMemo(
    () => views.find((view) => view.dayId === selectedDayId)?.data ?? [],
    [views, selectedDayId],
  );

  const noPossiblities = useMemo(() => {
    return dayViews.every((view) => view.rows.length === 0);
  }, [dayViews]);

  return (
    <section className="flex flex-col" data-component="Timetable">
      <TimetableHeader hasError={errors.TIMESLOT_NOT_AVAILABLE} period={period} isMobile>
        {days.map((day) => {
          const hasSlots = views
            .find((view) => view.dayId === day.id)
            ?.data.some((view) => view.rows && view.rows.length > 0);

          return (
            <div
              className="relative flex w-full grow justify-center"
              data-component="TimetableDay"
              data-has-slots={hasSlots}
              key={day.id}
            >
              <div className="relative flex flex-col text-center">
                <span
                  className={clsx(
                    "uppercase",
                    isToday(day.date, timeZone) &&
                      "after:absolute after:right-[-6px] after:top-[-6px] after:h-[6px] after:w-[6px] after:rounded-full after:bg-brand-dark",
                    !hasSlots && "text-gray-300",
                  )}
                  data-component="TimetableDay-Weekday"
                >
                  {weekdayFormatter.format(day.date.toDate())}
                </span>

                <Button
                  className={clsx(
                    "h-[30px] w-[30px] text-center",
                    day.id === selectedDayId && "rounded-full bg-brand-dark text-white",
                    !hasSlots && "text-gray-300",
                  )}
                  data-component="TimetableDay-Date"
                  onPress={() => {
                    const calendarDate = toCalendarDate(day.date).toString();

                    sendCAEvent({
                      type: "timetable-navigation",
                      payload: {
                        type: "date-selection",
                        startDay: calendarDate,
                        endDay: calendarDate,
                        timeZone: timeZone,
                      },
                    });

                    setSelectedDayId(day.id);
                  }}
                >
                  {dayFormatter.format(day.date.toDate())}
                </Button>
              </div>
            </div>
          );
        })}
      </TimetableHeader>

      <div>
        <div className="w-full p-sm text-center font-semibold">
          {selectedDay && fullDateFormatter.format(selectedDay.date.toDate())}
        </div>
        {noPossiblities ? (
          <div className="py-xs text-center italic text-gray-500" data-component="NoPossibilities">
            <Text i18nKey="steps.Timetable.DayPart.NoPossibilities" />
          </div>
        ) : (
          <div className="flex flex-col gap-sm">
            {dayViews.map((view) => (
              <TimetableDayView key={view.name} selectedDayId={selectedDayId} view={view} />
            ))}
          </div>
        )}
      </div>

      {(office.phone || office.email) && (
        <OfficeInformation email={office.email} phone={office.phone} />
      )}
    </section>
  );
}

function getDayTimeframes(groups: TimetableGroups, dayId: string) {
  function filterVisibleMobileSlots(timeframe: Timeframe, dayId: string) {
    const el = timeframe.timeSlots.find((slot) => slot.day.id === dayId);

    return Boolean(el && el.data);
  }

  const morningRows = groups.MORNING.timeframes.filter((timeframe: Timeframe) =>
    filterVisibleMobileSlots(timeframe, dayId),
  );
  const noonRows = groups.NOON.timeframes.filter((timeframe: Timeframe) =>
    filterVisibleMobileSlots(timeframe, dayId),
  );
  const eveningRows = groups.EVENING.timeframes.filter((timeframe: Timeframe) =>
    filterVisibleMobileSlots(timeframe, dayId),
  );

  return [morningRows, noonRows, eveningRows] as const;
}

interface View {
  name: string;
  rows: Timeframe[];
  Icon: React.ComponentType;
}

interface TimetableDayViewProps {
  view: View;
  selectedDayId?: string;
}

function TimetableDayView({ selectedDayId, view }: TimetableDayViewProps) {
  const { timeZone } = useTimetableContext();

  const [isOpen, setIsOpen] = useState(true);
  const formatter = useDateFormatter({ timeStyle: "short", timeZone });

  const viewTitle = useMemo(() => {
    if (view.name === "steps.Timetable.DayPart.morning") {
      return <Text i18nKey="steps.Timetable.DayPart.morning" />;
    }
    if (view.name === "steps.Timetable.DayPart.afternoon") {
      return <Text i18nKey="steps.Timetable.DayPart.afternoon" />;
    }
    if (view.name === "steps.Timetable.DayPart.evening") {
      return <Text i18nKey="steps.Timetable.DayPart.evening" />;
    }
  }, [view]);

  if (view.rows.length === 0) {
    return null;
  }

  return (
    <Collapsible
      className="flex flex-col gap-sm"
      header={
        <Button
          className="w-full"
          data-component="TimetableDayPart"
          onPress={() => setIsOpen((o) => !o)}
        >
          <div className={clsx("flex items-center justify-between font-semibold")}>
            <ChevronIcon
              className="text-black"
              direction={isOpen ? DIRECTION.UP : DIRECTION.DOWN}
            />
            {viewTitle}
            <view.Icon />
          </div>
        </Button>
      }
      isOpen={isOpen}
    >
      <div className="flex flex-col items-center justify-center gap-sm">
        {view.rows.map((timeframe) => {
          const slot = timeframe.timeSlots.find(
            (timeSlot) => timeSlot.day.id === selectedDayId && timeSlot.data,
          );

          if (!slot?.data) return null;

          return (
            <TimeslotLink
              className="w-full flex-1 rounded-lg border p-sm text-center text-brand hover:bg-gray-100"
              data-component="TimetableSlot"
              key={slot.id}
              slot={slot}
            >
              {(isLoading) =>
                isLoading ? (
                  <div className="my-[2px] flex justify-center">
                    <Spinner size={Spinner.size.SMALL} />
                  </div>
                ) : (
                  slot?.data &&
                  formatter.formatRange(slot.data.start.toDate(), slot.data.end.toDate())
                )
              }
            </TimeslotLink>
          );
        })}
      </div>
    </Collapsible>
  );
}
